import React, { useEffect, useState } from "react";
import Heros from "../components/Heros";
import AboutImg from "../assets/Intra-Assia9.jpg";
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import axios from "axios";

const IntraAsia = () => {
  // State for vessels and loading
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extraFields, setExtraFields] = useState([]);

  // Fetch vessel data from API
  useEffect(() => {
    const fetchVessels = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First check if schema is available to get any extra fields
        try {
          const schemaResponse = await axios.get('https://site2.akms.org.in/api/intra-asia/schema');
          // Filter out standard fields we already display
          const standardFields = ['id', 'vessel_name', 'voyage', 'lifting_capacity', 'calling_ports', 'etd_dates', 'lolo'];
          const extraColumns = schemaResponse.data
            .filter(col => col.VISIBLE !== false && !standardFields.includes(col.COLUMN_NAME))
            .map(col => col.COLUMN_NAME);
          
          setExtraFields(extraColumns);
        } catch (schemaError) {
          console.log('Schema endpoint not available or error:', schemaError);
          // If schema endpoint isn't available, continue without extra fields
        }
        
        // Fetch vessels from API
        const response = await axios.get('https://site2.akms.org.in/api/intra-asia');
        setVessels(response.data);
      } catch (error) {
        console.error('Error fetching vessels:', error);
        setError('Failed to load vessel data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchVessels();
  }, []);

  // Parse ports and dates into arrays
  const parseCommaList = (commaList) => {
    if (!commaList) return [];
    return commaList.split(',').map(item => item.trim());
  };

  // Format column name for display (snake_case to Title Case)
  const formatColumnName = (column) => {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <>
      <Navbar
        Home="Home"
        About="Sailing Schedule"
        Contact="Enquiry"
        Fleet="Break Bulk"
        PCTC="PCTC"
        More="Schedule"
        IntraAsia="Intra-Asia"
        Bay_Bengal="Bay of Bengal"
        Login="Login"
        PCTC2="PCTC"
      />

      <Heros
        cName="hero-mid"
        heroImg={AboutImg}
        url="/"
      />

      <div style={styles.pageContainer}>
        <div style={styles.titleContainer}>
          <h1 style={styles.pageTitle}>Sailing Schedule</h1>
        </div>

        {loading ? (
          <div style={styles.loadingMessage}>Loading vessel data...</div>
        ) : error ? (
          <div style={styles.errorMessage}>{error}</div>
        ) : vessels.length === 0 ? (
          <div style={styles.noDataMessage}>No vessels available at this time.</div>
        ) : (
          <div style={styles.tableWrapper}>
            {vessels.map((vessel, index) => {
              const ports = parseCommaList(vessel.calling_ports);
              const dates = parseCommaList(vessel.etd_dates);
              
              return (
                <div key={vessel.id || index} style={styles.scheduleContainer}>
                  <div style={styles.headerRow}>
                    <div 
                      style={styles.vesselNameCell}
                      title={`Vessel: ${vessel.vessel_name}${vessel.lolo ? " (LOLO)" : ""}`}
                    >
                      {vessel.vessel_name} {vessel.lolo ? "(LOLO)" : ""}
                    </div>
                    <div style={styles.voyageCell}>
                      <div>Voyage</div>
                      <div title={`Voyage: ${vessel.voyage}`}>{vessel.voyage}</div>
                    </div>
                    <div style={styles.capacityCell}>
                      <div>LIFTING CAPACITY</div>
                      <div title={`Lifting Capacity: ${vessel.lifting_capacity}`}>{vessel.lifting_capacity}</div>
                    </div>
                  </div>
                  
                  <div style={styles.tableScrollContainer}>
                    <table style={styles.dataTable}>
                      <tbody>
                        <tr>
                          <td style={styles.labelCell} title="Calling Ports">Calling ports</td>
                          {ports.map((port, i) => (
                            <td 
                              key={`port-${i}`} 
                              style={styles.dataCell} 
                              title={`Vessel: ${vessel.vessel_name} | Port: ${port} | Date: ${dates[i] || 'N/A'}`}
                            >
                              {port}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={styles.labelCell} title="Estimated Date">Est. date</td>
                          {dates.map((date, i) => (
                            <td 
                              key={`date-${i}`} 
                              style={styles.dataCell} 
                              title={`Vessel: ${vessel.vessel_name} | Port: ${ports[i] || 'N/A'} | Date: ${date}`}
                            >
                              {date}
                            </td>
                          ))}
                        </tr>
                        
                        {/* Render any extra fields that might have been added */}
                        {extraFields.map(field => (
                          <tr key={field}>
                            <td 
                              style={styles.labelCell}
                              title={formatColumnName(field)}
                            >
                              {formatColumnName(field)}
                            </td>
                            <td 
                              style={{...styles.dataCell, textAlign: 'left'}} 
                              colSpan={Math.max(ports.length, dates.length)}
                              title={`Vessel: ${vessel.vessel_name} | ${formatColumnName(field)}: ${vessel[field] || '-'}`}
                            >
                              {vessel[field] || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </>
  );
};

// Inline styles object - enhanced with better scroll functionality
const styles = {
  pageContainer: {
    width: "100%",
    backgroundColor: "#f5f5f5",
    padding: "0 0 40px 0",
  },
  titleContainer: {
    width: "100%",
    textAlign: "center",
    padding: "20px 0",
  },
  pageTitle: {
    color: "#e74c3c",
    fontSize: "28px",
    fontWeight: "500",
    margin: 0,
  },
  tableWrapper: {
    width: "95%",
    maxWidth: "1400px",
    margin: "0 auto",
    backgroundColor: "white",
    borderRadius: "4px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  scheduleContainer: {
    marginBottom: "1px",
    borderBottom: "1px solid #e0e0e0",
  },
  headerRow: {
    display: "flex",
    backgroundColor: "#2c3e50",
    color: "white",
    fontWeight: "bold",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  vesselNameCell: {
    width: "200px",
    padding: "10px 15px",
    textTransform: "uppercase",
    borderRight: "1px solid #3d5166",
    display: "flex",
    alignItems: "center",
    position: "relative",
    cursor: "default",
  },
  voyageCell: {
    width: "100px",
    padding: "5px 10px",
    textAlign: "center",
    borderRight: "1px solid #3d5166",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    cursor: "default",
  },
  capacityCell: {
    flex: "1",
    padding: "5px 10px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    cursor: "default",
  },
  tableScrollContainer: {
    overflowX: "auto",
    width: "100%",
    position: "relative",
    scrollbarWidth: "thin",
    scrollbarColor: "#90A4AE #CFD8DC",
  },
  dataTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "100%", // Ensures table is at least as wide as container
  },
  labelCell: {
    width: "120px",
    padding: "10px 15px",
    backgroundColor: "#f8f9fa",
    borderRight: "1px solid #dee2e6",
    borderBottom: "1px solid #dee2e6",
    textAlign: "left",
    verticalAlign: "middle",
    position: "sticky",
    left: 0,
    zIndex: 5,
    cursor: "default",
  },
  dataCell: {
    padding: "10px 15px",
    textAlign: "center",
    borderRight: "1px solid #dee2e6",
    borderBottom: "1px solid #dee2e6",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    position: "relative",
    cursor: "default",
    minWidth: "120px", // Ensure cells have minimum width
  },
  loadingMessage: {
    textAlign: "center",
    padding: "30px",
    fontSize: "16px",
  },
  errorMessage: {
    textAlign: "center",
    padding: "30px",
    color: "#e74c3c",
    fontSize: "16px",
  },
  noDataMessage: {
    textAlign: "center",
    padding: "30px",
    color: "#666",
    fontSize: "16px",
  }
};

export default IntraAsia;